import axios, { AxiosInstance } from "axios";

/**
 * Azure Function Service Module
 * Handles API calls to Azure Function for vendor and PO operations
 *
 * Configuration:
 * Update the following environment variables:
 * - VITE_AZURE_FUNCTION_URL: Your Azure Function endpoint URL
 * - VITE_AZURE_FUNCTION_KEY: Azure Function API key (if required for authentication)
 */

// Configuration - UPDATE WITH YOUR AZURE FUNCTION CREDENTIALS
const AZURE_FUNCTION_CONFIG = {
	BASE_URL:
		import.meta.env.VITE_AZURE_FUNCTION_URL ||
		"https://your-function.azurewebsites.net/api/handler",
	API_KEY: import.meta.env.VITE_AZURE_FUNCTION_KEY || "",
	ENDPOINT: import.meta.env.VITE_AZURE_FUNCTION_ENDPOINT || "",
};

const parseNetSuiteCheckbox = (value: unknown): boolean => {
	if (
		value === true ||
		value === 1 ||
		value === "1" ||
		value === "T" ||
		value === "t" ||
		value === "true" ||
		value === "TRUE" ||
		value === "Y" ||
		value === "y" ||
		value === "yes" ||
		value === "YES"
	) {
		return true;
	}

	if (value && typeof value === "object") {
		const maybeRecord = value as Record<string, unknown>;
		const nestedValue =
			maybeRecord.value ?? maybeRecord.text ?? maybeRecord.name;
		return parseNetSuiteCheckbox(nestedValue);
	}

	return false;
};

interface VendorOption {
	id: string;
	name: string;
	address?: string;
	city?: string;
	state?: string;
	zip?: string;
}

interface LocationOption {
	id: string;
	name: string;
}

interface DepartmentOption {
	id: string;
	name: string;
	hasPoApprovalSettings: boolean;
}

interface ProductionLineOption {
	id: string;
	name: string;
}

interface ClassOption {
	id: string;
	name: string;
}

interface AccountOption {
	id: string;
	number: string;
	name: string;
}

interface CustomerOption {
	id: string;
	name: string;
	classId?: string;
}

interface EmployeeOption {
	id: string;
	name: string;
	email: string;
}

interface PurchaseOrder {
	internalId: string;
	vendor: string;
	vendorId: string;
	date: string;
	dueDate?: string;
	amount: string;
	status: string;
	approvalStatus: string;
	nextApprover: string;
	nextApproverEmail: string;
	documentNumber: string;
	poSubmitter?: string;
	poSubmitterEmail?: string;
	poRequestorName?: string;
	memo?: string;
	location?: string;
	rejectionReason?: string;
	approvalRoute?: Array<{
		name: string;
		user: number;
		approved: string;
		approved_on: string;
		approved_by: string;
	}>;
	lineItems?: Array<{
		lineId: number;
		lineUniqueKey?: string;
		sysId?: string;
		account: string;
		customer: string;
		memo: string;
		amount: string;
	}>;
}

interface PurchaseOrderDetails {
	internalId: string;
	vendor: string;
	vendorId: string;
	date: string;
	dueDate: string;
	amount: string;
	status: string;
	approvalStatus: string;
	location: string;
	locationId: string;
	department: string;
	departmentId: string;
	productionLine: string;
	productionLineId: string;
	memo: string;
	expenseLines: Array<{
		lineId?: string;
		lineUniqueKey?: string;
		account: string;
		accountId: string;
		customer: string;
		customerId: string;
		amount: number;
		memo: string;
		department: string;
		departmentId: string;
		class: string;
		classId: string;
	}>;
	documentNumber: string;
}

interface CreatePOResponse {
	success: boolean;
	recordId?: string;
	message: string;
}

interface ExpenseLinePayload {
	account: string;
	customer: string;
	amount: number;
	memo: string;
	department: string;
	class: string;
}

type UpdatePOExpenseOperation = "add" | "update" | "delete";

interface UpdatePOExpenseChange {
	operation: UpdatePOExpenseOperation;
	lineId?: string;
	lineUniqueKey?: string;
	account?: string;
	customer?: string;
	amount?: number;
	memo?: string;
	department?: string;
	class?: string;
}

interface CreatePOPayload {
	vendor: string;
	duedate: string;
	location: string;
	department: string;
	productionLine: string;
	memo: string;
	accountName: string;
	userName: string;
	expenseList: ExpenseLinePayload[];
	attachments?: Array<{
		name: string;
		type: string;
		data: string;
	}>;
}

interface UpdatePOPayload extends CreatePOPayload {
	internalId: string;
	expenseChanges?: UpdatePOExpenseChange[];
}

class NetSuiteService {
	private axiosInstance: AxiosInstance;

	constructor() {
		//console.log("BASE_URL:", AZURE_FUNCTION_CONFIG.BASE_URL);
		//console.log("ENDPOINT:", AZURE_FUNCTION_CONFIG.ENDPOINT);
		this.axiosInstance = axios.create({
			baseURL: AZURE_FUNCTION_CONFIG.BASE_URL,
			headers: {
				"Content-Type": "application/json",
			},
		});

		// Add API key header if provided
		if (AZURE_FUNCTION_CONFIG.API_KEY) {
			this.axiosInstance.defaults.headers.common["x-functions-key"] =
				AZURE_FUNCTION_CONFIG.API_KEY;
		}
	}

	/**
	 * Fetch vendors from Azure Function
	 * Sends GET request with type=vendor parameter and endpoint
	 */
	async fetchVendors(): Promise<VendorOption[]> {
		try {
			const response = await this.axiosInstance.get("", {
				params: {
					type: "vendor",
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				},
			});
			// Check for outer success
			if (!response.data || !response.data.success) {
				throw new Error("Azure Function failed to execute");
			}
			// Check for nested error from NetSuite
			if (response.data.data && !response.data.data.success) {
				const errorMsg =
					response.data.data.error || "NetSuite operation failed";
				throw new Error(errorMsg);
			}

			// Transform response to our VendorOption format
			if (response.data.data && Array.isArray(response.data.data.vendors)) {
				return response.data.data.vendors.map((vendor: any) => ({
					id: vendor.internalId,
					name: vendor.entityId,
					address: vendor.address,
					city: vendor.city,
					state: vendor.state,
					zip: vendor.zip,
				}));
			}

			throw new Error("Invalid response format: vendors array not found");
		} catch (error: any) {
			console.error("Error fetching vendors from Azure Function:", error);
			throw new Error(error.message || "Failed to fetch vendors");
		}
	}

	/**
	 * Fetch employees from Azure Function
	 * Sends GET request with type=employee parameter and endpoint (without employeeId)
	 */
	async fetchEmployees(): Promise<EmployeeOption[]> {
		try {
			const response = await this.axiosInstance.get("", {
				params: {
					type: "employee",
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				},
			});

			if (!response.data || !response.data.success) {
				throw new Error("Azure Function failed to execute");
			}

			if (response.data.data && !response.data.data.success) {
				const errorMsg =
					response.data.data.error || "NetSuite operation failed";
				throw new Error(errorMsg);
			}

			const payload = response.data.data || response.data;
			const employees =
				payload.employees || payload.employeeList || payload.results || [];

			if (Array.isArray(employees)) {
				const mappedEmployees = employees
					.map((employee: any) => ({
						id: String(employee.internalId || employee.id || ""),
						name:
							employee.name ||
							`${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
							`Employee ${employee.internalId || employee.id || ""}`,
						email: String(employee.email || ""),
					}))
					.filter((employee: EmployeeOption) => employee.email);

				return mappedEmployees;
			}

			throw new Error("Invalid response format: employees array not found");
		} catch (error: any) {
			console.error("Error fetching employees from Azure Function:", error);
			throw new Error(error.message || "Failed to fetch employees");
		}
	}

	/**
	 * Fetch locations from Azure Function
	 * Sends GET request with type=location parameter and endpoint
	 */
	async fetchLocations(): Promise<LocationOption[]> {
		try {
			const response = await this.axiosInstance.get("", {
				params: {
					type: "location",
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				},
			});
			// Check for outer success
			if (!response.data || !response.data.success) {
				throw new Error("Azure Function failed to execute");
			}
			// Check for nested error from NetSuite
			if (response.data.data && !response.data.data.success) {
				const errorMsg =
					response.data.data.error || "NetSuite operation failed";
				throw new Error(errorMsg);
			}
			// Transform response to our LocationOption format
			if (response.data.data && Array.isArray(response.data.data.locations)) {
				return response.data.data.locations.map((location: any) => ({
					id: location.internalId,
					name: location.name,
				}));
			}
			throw new Error("Invalid response format: locations array not found");
		} catch (error: any) {
			console.error("Error fetching locations from Azure Function:", error);
			throw new Error(error.message || "Failed to fetch locations");
		}
	}

	/**
	 * Fetch departments from Azure Function
	 * Sends GET request with type=department parameter and endpoint
	 */
	async fetchDepartments(): Promise<DepartmentOption[]> {
		try {
			console.log("[Departments] Requesting department list", {
				type: "department",
				endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
			});

			const response = await this.axiosInstance.get("", {
				params: {
					type: "department",
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				},
			});

			console.log("[Departments] Raw response received", {
				outerSuccess: response?.data?.success,
				nestedSuccess: response?.data?.data?.success,
				hasDepartmentsArray: Array.isArray(response?.data?.data?.departments),
				departmentCount: Array.isArray(response?.data?.data?.departments)
					? response.data.data.departments.length
					: 0,
			});
			if (Array.isArray(response?.data?.data?.departments)) {
				console.log(
					"[Departments] Raw departments payload",
					response.data.data.departments,
				);
			}
			// Check for outer success
			if (!response.data || !response.data.success) {
				throw new Error("Azure Function failed to execute");
			}
			// Check for nested error from NetSuite
			if (response.data.data && !response.data.data.success) {
				const errorMsg =
					response.data.data.error || "NetSuite operation failed";
				throw new Error(errorMsg);
			}
			// Transform response to our DepartmentOption format
			if (response.data.data && Array.isArray(response.data.data.departments)) {
				const mappedDepartments: DepartmentOption[] =
					response.data.data.departments.map((department: any) => ({
						hasPoApprovalSettings: parseNetSuiteCheckbox(
							department.hasPOApprovalSettings ??
								department.custrecord_ccc_has_po_approval_settings,
						),
						id: department.internalId,
						name: department.name,
					}));

				console.log("[Departments] Mapped departments", {
					mappedCount: mappedDepartments.length,
					approvalEnabledCount: mappedDepartments.filter(
						(department) => department.hasPoApprovalSettings,
					).length,
					sample: mappedDepartments.slice(0, 3),
				});

				return mappedDepartments;
			}
			throw new Error("Invalid response format: departments array not found");
		} catch (error: any) {
			console.error("Error fetching departments from Azure Function:", error);
			throw new Error(error.message || "Failed to fetch departments");
		}
	}

	/**
	 * Fetch production lines from Azure Function
	 * Sends GET request with type=prodline parameter and endpoint
	 */
	async fetchProductionLines(): Promise<ProductionLineOption[]> {
		try {
			const response = await this.axiosInstance.get("", {
				params: {
					type: "productionlineno",
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				},
			});
			// Check for outer success
			if (!response.data || !response.data.success) {
				throw new Error("Azure Function failed to execute");
			}
			// Check for nested error from NetSuite
			if (response.data.data && !response.data.data.success) {
				const errorMsg =
					response.data.data.error || "NetSuite operation failed";
				throw new Error(errorMsg);
			}
			// Transform response to our ProductionLineOption format
			if (
				response.data.data &&
				Array.isArray(response.data.data.productionLines)
			) {
				return response.data.data.productionLines.map((prodline: any) => ({
					id: prodline.internalId,
					name: prodline.name,
				}));
			}
			throw new Error(
				"Invalid response format: productionLines array not found",
			);
		} catch (error: any) {
			console.error(
				"Error fetching production lines from Azure Function:",
				error,
			);
			throw new Error(error.message || "Failed to fetch production lines");
		}
	}

	/**
	 * Fetch classes from Azure Function
	 * Sends GET request with type=class parameter and endpoint
	 */
	async fetchClasses(): Promise<ClassOption[]> {
		try {
			const response = await this.axiosInstance.get("", {
				params: {
					type: "class",
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				},
			});
			//console.log("Class response:", response.data);
			// Check for outer success
			if (!response.data || !response.data.success) {
				throw new Error("Azure Function failed to execute");
			}
			// Check for nested error from NetSuite
			if (response.data.data && !response.data.data.success) {
				const errorMsg =
					response.data.data.error || "NetSuite operation failed";
				throw new Error(errorMsg);
			}
			// Transform response to our ClassOption format
			if (response.data.data && Array.isArray(response.data.data.classes)) {
				return response.data.data.classes.map((cls: any) => ({
					id: cls.internalId,
					name: cls.name,
				}));
			}
			throw new Error("Invalid response format: classes array not found");
		} catch (error: any) {
			console.error("Error fetching classes from Azure Function:", error);
			throw new Error(error.message || "Failed to fetch classes");
		}
	}

	/**
	 * Fetch accounts from Azure Function
	 * Sends GET request with type=account parameter and endpoint
	 */
	async fetchAccounts(): Promise<AccountOption[]> {
		try {
			const response = await this.axiosInstance.get("", {
				params: {
					type: "account",
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				},
			});
			// Check for outer success
			if (!response.data || !response.data.success) {
				throw new Error("Azure Function failed to execute");
			}
			// Check for nested error from NetSuite
			if (response.data.data && !response.data.data.success) {
				const errorMsg =
					response.data.data.error || "NetSuite operation failed";
				throw new Error(errorMsg);
			}
			// Transform response to our AccountOption format
			if (response.data.data && Array.isArray(response.data.data.accounts)) {
				return response.data.data.accounts.map((account: any) => ({
					id: account.internalId,
					name: account.name,
					number: account.number,
				}));
			}
			throw new Error("Invalid response format: accounts array not found");
		} catch (error: any) {
			console.error("Error fetching accounts from Azure Function:", error);
			throw new Error(error.message || "Failed to fetch accounts");
		}
	}

	/**
	 * Fetch customers from Azure Function
	 * Sends GET request with type=customer parameter and endpoint
	 */
	async fetchCustomers(): Promise<CustomerOption[]> {
		try {
			const response = await this.axiosInstance.get("", {
				params: {
					type: "customer",
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				},
			});
			// Check for outer success
			if (!response.data || !response.data.success) {
				throw new Error("Azure Function failed to execute");
			}
			// Check for nested error from NetSuite
			if (response.data.data && !response.data.data.success) {
				const errorMsg =
					response.data.data.error || "NetSuite operation failed";
				throw new Error(errorMsg);
			}
			// Transform response to our CustomerOption format
			if (response.data.data && Array.isArray(response.data.data.customers)) {
				return response.data.data.customers.map((customer: any) => ({
					id: customer.internalId,
					name: customer.companyName,
					classId: customer.classId,
				}));
			}
			throw new Error("Invalid response format: customers array not found");
		} catch (error: any) {
			console.error("Error fetching customers from Azure Function:", error);
			throw new Error(error.message || "Failed to fetch customers");
		}
	}

	/**
	 * Create a PO via Azure Function
	 * Sends POST request with vendor ID, location, department, production line, memo, and expense lines
	 */
	async createPO(payload: CreatePOPayload): Promise<CreatePOResponse> {
		try {
			const requestBody = {
				endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				vendor: payload.vendor,
				duedate: payload.duedate,
				location: payload.location,
				department: payload.department,

				productionLine: payload.productionLine,
				memo: payload.memo,
				accountName: payload.accountName,
				userName: payload.userName,
				expenseList: payload.expenseList,
				...(payload.attachments &&
					payload.attachments.length > 0 && {
						attachments: payload.attachments,
					}),
			};
			//console.log("Create PO request body:", requestBody);

			const response = await this.axiosInstance.post("", requestBody);
			//console.log("Create PO response:", response);

			const createdId =
				response.data?.data?.recordId ??
				response.data?.data?.id ??
				response.data?.id;
			if (createdId) {
				return {
					success: true,
					recordId: String(createdId),
					message: `PO created successfully with ID: ${createdId}`,
				};
			}

			return {
				success: true,
				message: "PO created successfully",
			};
		} catch (error: any) {
			console.error("Error creating PO via Azure Function:", error);
			// Build detailed error message for UI
			let errorMessage = "Failed to create PO";

			if (error.response?.data) {
				console.error("❌ Azure Function error response:", error.response.data);
				console.error(
					"   Full error.response.data:",
					JSON.stringify(error.response.data, null, 2),
				);
				console.error(
					"   error.response.data.error =",
					error.response.data.error,
				);
				console.error(
					"   error.response.data.message =",
					error.response.data.message,
				);
				console.error(
					"   error.response.data.data =",
					error.response.data.data,
				);
				console.error(
					"   error.response.data.data?.error =",
					error.response.data.data?.error,
				);
				// Include status code and Azure error in the message
				const azureError =
					error.response.data.data?.error ||
					error.response.data.error ||
					error.response.data.message ||
					"Unknown error";
				console.error("   azureError selected as:", azureError);
				errorMessage = `Error (${error.response.status}): ${azureError}`;
			} else if (error.response) {
				// Has response but no data
				errorMessage = `Error (${error.response.status}): ${error.response.statusText || "Request failed"}`;
			} else {
				// Network error or other
				errorMessage = error.message || "Failed to create PO";
			}

			return {
				success: false,
				message: errorMessage,
			};
		}
	}

	/**
	 * Fetch purchase orders from Azure Function
	 * When called with just email, returns list of POs
	 * When called with email and internalId, returns detailed PO information
	 */
	async fetchPurchaseOrders(email: string): Promise<PurchaseOrder[]>;
	async fetchPurchaseOrders(
		email: string,
		options: { vendorId?: string; searchAll?: "T" },
	): Promise<PurchaseOrder[]>;
	async fetchPurchaseOrders(
		email: string,
		internalId: string,
	): Promise<PurchaseOrderDetails>;
	async fetchPurchaseOrders(
		email: string,
		internalIdOrOptions?: string | { vendorId?: string; searchAll?: "T" },
	): Promise<PurchaseOrder[] | PurchaseOrderDetails> {
		try {
			const params: Record<string, string> = {
				type: "purchaseorder",
				endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
			};

			const internalId =
				typeof internalIdOrOptions === "string"
					? internalIdOrOptions
					: undefined;
			const vendorId =
				typeof internalIdOrOptions === "object"
					? internalIdOrOptions.vendorId
					: undefined;
			const searchAll =
				typeof internalIdOrOptions === "object"
					? internalIdOrOptions.searchAll
					: undefined;

			// Add email or internalId to params
			if (internalId) {
				params.internalId = internalId;
			} else if (vendorId) {
				params.vendorId = vendorId;
			} else {
				params.email = email;
				if (searchAll) {
					params.searchAll = searchAll;
				}
			}

			const response = await this.axiosInstance.get("", { params });

			// Check for outer success
			if (!response.data || !response.data.success) {
				throw new Error("Azure Function failed to execute");
			}
			// Check for nested error from NetSuite
			if (response.data.data && !response.data.data.success) {
				const errorMsg =
					response.data.data.error || "NetSuite operation failed";
				throw new Error(errorMsg);
			}

			// Return detailed PO if internalId was provided
			if (
				internalId &&
				response.data.data &&
				response.data.data.purchaseOrder
			) {
				const po = response.data.data.purchaseOrder;
				// Map expense lines - API returns 'expenses' with fields like account, customer, etc.
				const mappedExpenseLines = (po.expenses || []).map((line: any) => ({
					lineId:
						line.lineId ||
						line.id ||
						line.line ||
						line.lineNum ||
						line.linenumber ||
						line.lineNumber ||
						"",
					lineUniqueKey:
						line.lineUniqueKey ||
						line.lineuniquekey ||
						line.lineUniqueId ||
						line.sys_id ||
						line.sysid ||
						line.uniquekey ||
						line.lineKey ||
						"",
					account: line.account || "",
					accountId: line.account || "", // API returns ID directly
					customer: line.customer || "",
					customerId: line.customer || "", // API returns ID directly
					amount: line.amount || 0,
					memo: line.memo || "",
					department: line.department || "",
					departmentId: line.department || "", // API returns ID directly
					class: line.class || "",
					classId: line.class || "", // API returns ID directly
				}));

				const result = {
					internalId: po.internalId || "",
					vendor: po.vendor || "", // Display name
					vendorId: po.vendor || "", // API returns vendor ID as 'vendor'
					date: po.date || "", // Date field
					dueDate: po.duedate ? po.duedate.split("T")[0] : "", // Extract just YYYY-MM-DD from ISO datetime
					amount: po.amount || "", // Amount field
					status: po.status || "",
					approvalStatus: po.approvalStatus || "",
					location: po.location || "", // Display name
					locationId: po.location || "", // API returns location ID as 'location'
					department: po.department || "", // Display name
					departmentId: po.department || "", // API returns department ID as 'department'
					productionLine: po.productionLineNo || "", // Display name
					productionLineId: po.productionLineNo || "", // API returns productionLineNo
					memo: po.memo || "",
					rejectionReason: po.rejectionReason || "",
					expenseLines: mappedExpenseLines,
					documentNumber: po.documentNumber || "",
				};
				return result;
			}

			// Return list of POs if no internalId
			if (
				response.data.data &&
				Array.isArray(response.data.data.purchaseOrders)
			) {
				const allRecords = response.data.data.purchaseOrders;
				console.log("[PO Search] RESTlet records returned:", allRecords.length);

				// Group records by internalId
				const poMap = new Map<string, any>();

				// Process all records
				allRecords.forEach((record: any) => {
					const lineId = parseInt(record.lineId) || 0;

					if (lineId === 0) {
						// Parse approvalRoute if it exists
						let approvalRoute: Array<{
							name: string;
							user: number;
							approved: string;
							approved_on: string;
							approved_by: string;
						}> = [];
						if (record.approvalRoute) {
							try {
								approvalRoute = JSON.parse(record.approvalRoute);
							} catch (e) {
								console.error("Failed to parse approvalRoute:", e);
							}
						}

						// This is a main PO record
						poMap.set(record.internalId, {
							internalId: record.internalId,
							vendor: record.vendor,
							vendorId: record.vendorId || "",
							date: record.date || "",
							dueDate: record.dueDate || "",
							amount: record.amount,
							status: record.status,
							approvalStatus: record.approvalStatus,
							nextApprover: record.nextApprover,
							nextApproverEmail: record.nextApproverEmail || "",
							documentNumber: record.documentNumber || "",
							poSubmitter: record.poSubmitter || "",
							poSubmitterEmail: record.poSubmitterEmail || "",
							poRequestorName: record.poRequestorName || "",
							memo: record.memo || "",
							location: record.location || "",
							rejectionReason: record.rejectionReason || "",
							approvalRoute: approvalRoute,
							lineItems: [],
						});
					}
				});

				// Add line items to their respective POs
				allRecords.forEach((record: any) => {
					const lineId = parseInt(record.lineId) || 0;

					if (lineId > 0) {
						// This is a line item
						const po = poMap.get(record.internalId);
						if (po) {
							po.lineItems.push({
								lineId: lineId,
								lineUniqueKey:
									record.lineUniqueKey ||
									record.lineuniquekey ||
									record.sys_id ||
									record.sysid ||
									"",
								sysId: record.sys_id || record.sysid || "",
								account: record.account || "",
								customer: record.vendor || "", // API returns vendor name in 'vendor' field
								memo: record.memo || "",
								amount: record.amount || "",
							});
						}
					}
				});

				const result = Array.from(poMap.values());
				console.log(
					"[PO Search] Unique PO count after grouping:",
					result.length,
				);
				console.log(
					"[PO Search] PO email summary:",
					result.map((po) => ({
						internalId: po.internalId,
						documentNumber: po.documentNumber,
						poSubmitterEmail: po.poSubmitterEmail || "",
						nextApproverEmail: po.nextApproverEmail || "",
					})),
				);
				return result;
			}

			throw new Error("Invalid response format: purchase order data not found");
		} catch (error: any) {
			console.error(
				"Error fetching purchase orders from Azure Function:",
				error,
			);
			throw new Error(error.message || "Failed to fetch purchase orders");
		}
	}

	/**
	 * Fetch PO PDF from Azure Function
	 * Sends GET request with type=pdf and internalId
	 * Returns the PDF as a Blob for download
	 */
	async fetchPOPdf(internalId: string): Promise<Blob> {
		try {
			const response = await this.axiosInstance.get("", {
				params: {
					type: "pdf",
					internalId: internalId,
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				},
			});

			// Check for outer success
			if (!response.data || !response.data.success) {
				throw new Error("Azure Function failed to execute");
			}

			// Check for nested error from NetSuite
			if (response.data.data && !response.data.data.success) {
				const errorMsg =
					response.data.data.error || "NetSuite operation failed";
				throw new Error(errorMsg);
			}

			// Extract the base64 PDF content from the response
			const pdfData = response.data.data;
			if (!pdfData || !pdfData.pdfContent) {
				throw new Error("No PDF content in response");
			}

			// Convert base64 string to Blob
			const binaryString = atob(pdfData.pdfContent);
			const bytes = new Uint8Array(binaryString.length);
			for (let i = 0; i < binaryString.length; i++) {
				bytes[i] = binaryString.charCodeAt(i);
			}

			const blob = new Blob([bytes], { type: "application/pdf" });
			return blob;
		} catch (error: any) {
			console.error("Error fetching PO PDF from Azure Function:", error);
			throw new Error(error.message || "Failed to fetch PO PDF");
		}
	}

	/**
	 * Check authorization for a PO via Azure Function
	 * Sends GET request with type=poauthorization and PO details
	 * Returns true if authorized, throws error if not
	 */
	async checkPOAuthorization(
		poId: string,
		accountName: string,
		userName: string,
	): Promise<boolean> {
		try {
			const response = await this.axiosInstance.get("", {
				params: {
					type: "poauthorization",
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
					poId: poId,
					accountName: accountName,
					userName: userName,
				},
			});

			// Check for outer success
			if (!response.data || !response.data.success) {
				throw new Error(
					"Authorization check failed: Azure Function did not respond successfully",
				);
			}

			// Check if user is authorized (nested in data)
			if (response.data.data && response.data.data.isAuthorized === false) {
				throw new Error("You are not authorized to access this purchase order");
			}

			return true;
		} catch (error: any) {
			console.error("PO Authorization check failed:", error);
			throw new Error(error.message || "Not authorized to access this PO");
		}
	}

	/**
	 * Create an Item Receipt from an approved PO via Azure Function
	 * Sends POST request with type=itemreceipt, poId, and memo
	 */
	async createItemReceipt(
		poId: string,
		memo: string,
		date?: string,
	): Promise<{ success: boolean; message: string; recordId?: number }> {
		try {
			const requestBody = {
				type: "itemreceipt",
				endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				poId,
				memo,
				...(date && { date }),
			};
			const response = await this.axiosInstance.post("", requestBody);

			if (response.data?.success) {
				return {
					success: true,
					message: response.data.message || "Item Receipt created successfully",
					recordId: response.data.recordId,
				};
			}

			return {
				success: false,
				message: response.data?.message || "Failed to create Item Receipt",
			};
		} catch (error: any) {
			console.error("Error creating Item Receipt via Azure Function:", error);
			const azureError =
				error.response?.data?.data?.error ||
				error.response?.data?.error ||
				error.response?.data?.message ||
				error.message ||
				"Failed to create Item Receipt";
			return { success: false, message: azureError };
		}
	}

	async receivePoItems(
		poId: string,
		expenseLines: Array<{ memo: string; amount: number }>,
		memo: string,
		date: string,
	): Promise<{ success: boolean; message: string; recordId?: number }> {
		try {
			const requestBody = {
				type: "receivepurchaseorderitems",
				endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				poId,
				expenseLines,
				memo,
				date,
			};
			const response = await this.axiosInstance.post("", requestBody);

			if (response.data?.success) {
				return {
					success: true,
					message:
						response.data.message ||
						"Purchase order items received successfully",
					recordId: response.data.recordId,
				};
			}

			return {
				success: false,
				message:
					response.data?.message || "Failed to receive purchase order items",
			};
		} catch (error: any) {
			console.error("Error receiving PO items via Azure Function:", error);
			const azureError =
				error.response?.data?.data?.error ||
				error.response?.data?.error ||
				error.response?.data?.message ||
				error.message ||
				"Failed to receive purchase order items";
			return { success: false, message: azureError };
		}
	}

	/**
	 * Get already-received items for a Purchase Order
	 * Fetches list of items that have been previously received
	 */
	async getReceivedItems(
		poId: string,
	): Promise<Array<{ memo: string; amount: string }>> {
		try {
			const response = await this.axiosInstance.get("", {
				params: {
					type: "receivedItems",
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
					poId,
				},
			});

			if (response.data?.data?.success && response.data?.data?.receipts) {
				return response.data.data.receipts;
			}

			return [];
		} catch (error: any) {
			console.error("Error fetching received items via Azure Function:", error);
			return [];
		}
	}

	/**
	 * Approve a Purchase Order via Azure Function
	 * Sends POST request to approve the PO
	 */
	async approvePurchaseOrder(
		poId: string,
	): Promise<{ success: boolean; message: string }> {
		try {
			const requestBody = {
				type: "approvepurchaseorder",
				endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				poId,
			};
			const response = await this.axiosInstance.post("", requestBody);

			if (response.data?.success) {
				return {
					success: true,
					message:
						response.data.message || "Purchase Order approved successfully",
				};
			}

			return {
				success: false,
				message: response.data?.message || "Failed to approve Purchase Order",
			};
		} catch (error: any) {
			console.error(
				"Error approving Purchase Order via Azure Function:",
				error,
			);
			const azureError =
				error.response?.data?.data?.error ||
				error.response?.data?.error ||
				error.response?.data?.message ||
				error.message ||
				"Failed to approve Purchase Order";
			return { success: false, message: azureError };
		}
	}

	/**
	 * Reject a Purchase Order via Azure Function
	 * Sends POST request to reject the PO with a rejection message
	 */
	async rejectPurchaseOrder(
		poId: string,
		rejectMessage: string,
	): Promise<{ success: boolean; message: string }> {
		try {
			const requestBody = {
				type: "rejectpurchaseorder",
				endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				poId,
				rejectMessage,
			};
			const response = await this.axiosInstance.post("", requestBody);

			if (response.data?.success) {
				return {
					success: true,
					message:
						response.data.message || "Purchase Order rejected successfully",
				};
			}

			return {
				success: false,
				message: response.data?.message || "Failed to reject Purchase Order",
			};
		} catch (error: any) {
			console.error(
				"Error rejecting Purchase Order via Azure Function:",
				error,
			);
			const azureError =
				error.response?.data?.data?.error ||
				error.response?.data?.error ||
				error.response?.data?.message ||
				error.message ||
				"Failed to reject Purchase Order";
			return { success: false, message: azureError };
		}
	}

	/**
	 * Update a PO via Azure Function
	 * Sends PUT request with updated PO details
	 */
	async updatePO(payload: UpdatePOPayload): Promise<CreatePOResponse> {
		try {
			const requestBody = {
				type: "updatepo",
				endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				internalId: payload.internalId,
				vendor: payload.vendor,
				duedate: payload.duedate,
				location: payload.location,
				department: payload.department,
				productionLine: payload.productionLine,
				memo: payload.memo,
				accountName: payload.accountName,
				userName: payload.userName,
				expenseList: payload.expenseList,
				...(payload.expenseChanges &&
					payload.expenseChanges.length > 0 && {
						expenseChanges: payload.expenseChanges,
					}),
				...(payload.attachments &&
					payload.attachments.length > 0 && {
						attachments: payload.attachments,
					}),
			};
			//console.log("Update PO request body:", requestBody);

			const response = await this.axiosInstance.put("", requestBody);
			//console.log("Update PO response:", response);

			const updatedId =
				response.data?.data?.recordId ??
				response.data?.data?.id ??
				response.data?.id;
			if (updatedId) {
				return {
					success: true,
					recordId: String(updatedId),
					message: `PO updated successfully with ID: ${updatedId}`,
				};
			}

			return {
				success: true,
				message: "PO updated successfully",
			};
		} catch (error: any) {
			console.error("Error updating PO via Azure Function:", error);
			// Build detailed error message for UI
			let errorMessage = "Failed to update PO";

			if (error.response?.data) {
				// Include status code and Azure error in the message
				const azureError =
					error.response.data.data?.error ||
					error.response.data.error ||
					error.response.data.message ||
					"Unknown error";
				console.error("   azureError selected as:", azureError);
				errorMessage = `Error (${error.response.status}): ${azureError}`;
			} else if (error.response) {
				// Has response but no data
				errorMessage = `Error (${error.response.status}): ${error.response.statusText || "Request failed"}`;
			} else {
				// Network error or other
				errorMessage = error.message || "Failed to update PO";
			}

			return {
				success: false,
				message: errorMessage,
			};
		}
	}

	/**
	 * Fetch employee name by employee ID via Azure Function
	 * Sends GET request with type=employee and employeeId
	 * Returns employee name or empty string if not found
	 */
	async fetchEmployeeName(employeeId: number | string): Promise<string> {
		try {
			const response = await this.axiosInstance.get("", {
				params: {
					type: "employee",
					employeeId: employeeId,
					endpoint: AZURE_FUNCTION_CONFIG.ENDPOINT,
				},
			});

			if (response.data?.success) {
				const payload = response.data?.data ?? response.data;

				if (payload?.employee?.name) {
					return payload.employee.name;
				}

				if (payload?.name) {
					return payload.name;
				}
			}

			return "";
		} catch (error: any) {
			console.error("Error fetching employee name:", error);
			return "";
		}
	}
}

// Export singleton instance
export const netsuiteService = new NetSuiteService();

export type {
	VendorOption,
	EmployeeOption,
	LocationOption,
	DepartmentOption,
	ProductionLineOption,
	ClassOption,
	AccountOption,
	CustomerOption,
	PurchaseOrder,
	PurchaseOrderDetails,
	ExpenseLinePayload,
	UpdatePOExpenseChange,
	UpdatePOExpenseOperation,
	CreatePOPayload,
	UpdatePOPayload,
	CreatePOResponse,
};
